import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Segmented, Input, Select, Pagination, Button, Empty, Spin, Tag } from 'antd';
import axios from 'axios';
import config from '../../config';
import { NdoeImage } from '../../components/ndoe';
import './catalogueSelecteur.css';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;

// Critères multi-sélection. Déclarés AU NIVEAU MODULE : un objet recréé dans le
// composant à chaque rendu invaliderait les dépendances des hooks.
const FILTRES_VIDES = Object.freeze({
  marque: [], categorie: [], matiere: [], couleur: [], taille: [],
});

// Les clés doivent correspondre à celles acceptées par l'API (liste blanche
// côté serveur : une valeur inconnue retombe silencieusement sur le tri par nom).
const TRIS = [
  { value: 'nom', label: 'Tri : A→Z' },
  { value: 'recent', label: 'Ajoutés récemment' },
  { value: 'ancien', label: 'Ajoutés il y a longtemps' },
  { value: 'stock_desc', label: 'Stock décroissant' },
  { value: 'stock_asc', label: 'Stock croissant' },
];

/**
 * MODE CATALOGUE (demande proprio 20/07) — parcourir avec les photos et cocher
 * PLUSIEURS articles d'un coup, au lieu de deviner un nom à la frappe.
 *
 * À la validation, deux façons d'utiliser la sélection :
 *   · « une série par article »  → chaque article devient sa propre courbe
 *     (comparaison) — dans la limite de places libres ;
 *   · « tout regrouper »         → les articles forment UNE série cumulée
 *     (« mes escarpins noirs » vus comme un ensemble).
 *
 * Props : open, onClose, placesLibres, onValider(series[]) où chaque série vaut
 * { type: 'produit'|'variante', ids: number[] }.
 */
export default function CatalogueSelecteur({ open, onClose, placesLibres = 5, onValider, typeInitial = 'produit', imposerRegroupement = false }) {
  const [type, setType] = useState(typeInitial);
  const [q, setQ] = useState('');
  // Filtres MULTI-sélection (demande du 20/07), sur le modèle du catalogue des
  // photos à partager : un tableau d'identifiants par critère.
  const [filtres, setFiltres] = useState(FILTRES_VIDES);
  // Hauteur de talon (#195, fusionné) : filtre SIMPLE — l'API n'accepte qu'une
  // valeur (dont « -1 » pour les modèles non renseignés), donc hors de `filtres`.
  const [hauteur, setHauteur] = useState(null);
  const [annees, setAnnees] = useState([null, null]);   // [min, max] de création
  const [tri, setTri] = useState('nom');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page_size: 24 });
  const [listes, setListes] = useState({
    marques: [], categories: [], matieres: [], couleurs: [], tailles: [], hauteurs: [], annees: null,
  });
  const [loading, setLoading] = useState(false);
  const [choisis, setChoisis] = useState([]);          // [{id, label, img}]
  const [regrouper, setRegrouper] = useState(false);
  const seq = useRef(0);

  // Changer de type ou de filtre repart de la première page — et la sélection
  // en cours est vidée quand on bascule Modèle ↔ Variante (des identifiants de
  // mondes différents ne peuvent pas cohabiter dans une même série).
  useEffect(() => { setPage(1); }, [type, q, filtres, hauteur, annees, tri]);
  useEffect(() => { setChoisis([]); }, [type]);
  // À l'OUVERTURE : on hérite du type de la série visée (sinon la fenêtre
  // proposait des modèles pour une série de variantes) et on remet à zéro les
  // réglages précédents — « regrouper » compris, dont la bascule est masquée
  // sous 2 articles et restait donc active à l'insu de l'utilisateur.
  useEffect(() => {
    if (open) {
      setType(typeInitial); setRegrouper(false); setChoisis([]); setQ('');
      setFiltres(FILTRES_VIDES); setHauteur(null); setAnnees([null, null]); setTri('nom'); setPage(1);
    }
  }, [open, typeInitial]);

  const charger = useCallback(async () => {
    if (!open) return;
    const mien = ++seq.current;
    setLoading(true);
    try {
      // Un critère vide est OMIS plutôt qu'envoyé vide : l'API ignorerait la
      // valeur, mais l'URL resterait illisible dans les journaux.
      const params = { type, page, page_size: 24, sort: tri };
      if (q.trim()) params.q = q.trim();
      Object.entries(filtres).forEach(([cle, vals]) => {
        if (vals && vals.length) params[cle] = vals.join(',');
      });
      // Hauteur : « -1 » (non renseignée) est volontairement truthy — on l'envoie.
      if (hauteur) params.hauteur = hauteur;
      if (annees[0]) params.annee_min = annees[0];
      if (annees[1]) params.annee_max = annees[1];

      const { data: d } = await axios.get(`${DOMAIN}/api/rapport/produits/evolution/catalogue`, { params });
      if (mien !== seq.current) return;
      setData({ items: d.items || [], total: d.total || 0, page_size: d.page_size || 24 });
      // Les référentiels ne voyagent qu'avec la 1re page : on ne les écrase pas
      // avec des tableaux vides en paginant.
      if (d.marques) {
        setListes({
          marques: d.marques || [], categories: d.categories || [], matieres: d.matieres || [],
          couleurs: d.couleurs || [], tailles: d.tailles || [], hauteurs: d.hauteurs || [], annees: d.annees || null,
        });
      }
    } catch (e) {
      if (mien === seq.current) setData({ items: [], total: 0, page_size: 24 });
    } finally {
      if (mien === seq.current) setLoading(false);
    }
  }, [open, type, q, filtres, hauteur, annees, tri, page]);

  // Recherche différée : on ne part pas en requête à chaque lettre tapée.
  useEffect(() => {
    const t = setTimeout(charger, q ? 320 : 0);
    return () => clearTimeout(t);
  }, [charger, q]);

  const estChoisi = useCallback((id) => choisis.some((c) => c.id === id), [choisis]);
  const basculer = (item) => {
    setChoisis((arr) => (arr.some((c) => c.id === item.id)
      ? arr.filter((c) => c.id !== item.id)
      : [...arr, { id: item.id, label: item.label, img: item.img }]));
  };

  // Combien de séries la validation va-t-elle créer ?
  // Remplir une série EXISTANTE ne peut donner qu'une série : la bascule est
  // masquée dans ce cas (elle promettait un découpage impossible à tenir).
  const groupe = regrouper || imposerRegroupement;
  const nbSeries = groupe ? (choisis.length ? 1 : 0) : choisis.length;
  const troppleines = nbSeries > placesLibres;

  const valider = () => {
    if (!choisis.length || troppleines) return;
    const ids = choisis.map((c) => c.id);
    // Les libellés voyagent avec la sélection : sans eux, l'écran afficherait
    // des NUMÉROS (les articles cochés ici n'ont pas transité par la recherche).
    const libelles = Object.fromEntries(choisis.map((c) => [c.id, c.label]));
    onValider(
      groupe ? [{ type, ids }] : ids.map((id) => ({ type, ids: [id] })),
      libelles,
    );
    onClose();
  };

  const opts = useCallback((liste) => (liste || []).map((x) => ({ value: x.id, label: x.nom })), []);
  const optionsMarque = useMemo(() => opts(listes.marques), [opts, listes.marques]);
  const optionsCategorie = useMemo(() => opts(listes.categories), [opts, listes.categories]);
  const optionsMatiere = useMemo(() => opts(listes.matieres), [opts, listes.matieres]);
  const optionsCouleur = useMemo(() => opts(listes.couleurs), [opts, listes.couleurs]);
  const optionsTaille = useMemo(
    () => (listes.tailles || []).map((t) => ({ value: t.id, label: `P${t.nom}` })), [listes.tailles],
  );
  // Hauteur (#195) : liste simple + l'option « Non renseignée » (-1).
  const optionsHauteur = useMemo(
    () => [...(listes.hauteurs || []).map((h) => ({ value: h.id, label: h.nom })), { value: -1, label: 'Non renseignée' }],
    [listes.hauteurs],
  );

  // Années réellement présentes en base : l'écran ne propose pas de plage vide.
  const optionsAnnee = useMemo(() => {
    const a = listes.annees;
    if (!a || !a.min || !a.max) return [];
    const out = [];
    for (let y = Number(a.max); y >= Number(a.min); y -= 1) out.push({ value: y, label: String(y) });
    return out;
  }, [listes.annees]);

  const setF = (cle) => (vals) => setFiltres((f) => ({ ...f, [cle]: vals || [] }));
  const nbFiltresActifs = Object.values(filtres).reduce((n, v) => n + (v?.length || 0), 0)
    + (hauteur ? 1 : 0) + (annees[0] ? 1 : 0) + (annees[1] ? 1 : 0);
  const reinitialiser = () => { setFiltres(FILTRES_VIDES); setHauteur(null); setAnnees([null, null]); setQ(''); setTri('nom'); };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={860}
      title="Catalogue — choisissez les articles à analyser"
      footer={null}
      destroyOnClose
    >
      <div className="cs-barre">
        <Segmented value={type} onChange={setType} options={[
          { value: 'produit', label: 'Modèles' },
          { value: 'variante', label: 'Variantes (pointures)' },
        ]} />
        <Input.Search allowClear placeholder="Nom ou code…" value={q}
          onChange={(e) => setQ(e.target.value)} className="cs-recherche" />
        <Select mode="multiple" allowClear maxTagCount={1} placeholder="Marque"
          value={filtres.marque} onChange={setF('marque')} options={optionsMarque}
          className="cs-filtre" showSearch optionFilterProp="label" />
        <Select mode="multiple" allowClear maxTagCount={1} placeholder="Catégorie"
          value={filtres.categorie} onChange={setF('categorie')} options={optionsCategorie}
          className="cs-filtre" showSearch optionFilterProp="label" />
        <Select mode="multiple" allowClear maxTagCount={1} placeholder="Matière"
          value={filtres.matiere} onChange={setF('matiere')} options={optionsMatiere}
          className="cs-filtre" showSearch optionFilterProp="label" />
        <Select mode="multiple" allowClear maxTagCount={1} placeholder="Couleur"
          value={filtres.couleur} onChange={setF('couleur')} options={optionsCouleur}
          className="cs-filtre" showSearch optionFilterProp="label" />
        <Select mode="multiple" allowClear maxTagCount={1} placeholder="Pointure"
          value={filtres.taille} onChange={setF('taille')} options={optionsTaille}
          className="cs-filtre cs-filtre-pt" />
        {/* Hauteur de talon (#195) : filtre simple, l'API n'en prend qu'une valeur. */}
        <Select allowClear placeholder="Hauteur de talon" value={hauteur} onChange={setHauteur}
          options={optionsHauteur} className="cs-filtre" />
      </div>

      <div className="cs-barre cs-barre-2">
        {/* Année d'AJOUT AU SYSTÈME du modèle. Les bornes proposées sont celles
            qui existent réellement en base, servies par l'API. */}
        <span className="cs-libelle">Ajouté entre</span>
        <Select allowClear placeholder="année" value={annees[0]} className="cs-annee"
          options={optionsAnnee.filter((o) => !annees[1] || o.value <= annees[1])}
          onChange={(v) => setAnnees(([, max]) => [v ?? null, max])} />
        <span className="cs-libelle">et</span>
        <Select allowClear placeholder="année" value={annees[1]} className="cs-annee"
          options={optionsAnnee.filter((o) => !annees[0] || o.value >= annees[0])}
          onChange={(v) => setAnnees(([min]) => [min, v ?? null])} />

        <Select value={tri} onChange={setTri} options={TRIS} className="cs-tri" title="Trier les articles" />

        {nbFiltresActifs > 0 ? (
          <Button size="small" type="link" onClick={reinitialiser}>
            Tout effacer ({nbFiltresActifs})
          </Button>
        ) : null}
      </div>

      {loading ? (
        <div className="cs-attente"><Spin /></div>
      ) : !data.items.length ? (
        <Empty description="Aucun article pour ces filtres" />
      ) : (
        <div className="cs-grille">
          {data.items.map((it) => {
            const actif = estChoisi(it.id);
            return (
              <button type="button" key={it.id} onClick={() => basculer(it)}
                className={`cs-carte ${actif ? 'is-choisi' : ''}`} title={it.label}>
                <span className="cs-carte__case">{actif ? '✓' : ''}</span>
                <NdoeImage src={it.img} alt={it.label} size="md" preview={false} />
                <span className="cs-carte__nom">{it.label}</span>
                <span className={`cs-carte__detail ${it.stock <= 0 ? 'is-vide' : ''}`}>{it.detail}</span>
              </button>
            );
          })}
        </div>
      )}

      {data.total > data.page_size && (
        <div className="cs-pages">
          <Pagination current={page} total={data.total} pageSize={data.page_size}
            onChange={setPage} showSizeChanger={false} size="small"
            showTotal={(t) => `${t} article(s)`} />
        </div>
      )}

      <div className="cs-pied">
        <div className="cs-pied__choix">
          {choisis.length ? (
            <>
              <b>{choisis.length} sélectionné(s)</b>
              <div className="cs-jetons">
                {choisis.slice(0, 4).map((c) => (
                  <Tag key={c.id} closable onClose={(e) => { e.preventDefault(); basculer(c); }}>
                    {c.label.slice(0, 28)}
                  </Tag>
                ))}
                {choisis.length > 4 && <Tag>+{choisis.length - 4}</Tag>}
              </div>
            </>
          ) : <span className="cs-vide">Cliquez les articles à comparer.</span>}
        </div>

        <div className="cs-pied__actions">
          {choisis.length > 1 && !imposerRegroupement && (
            <Segmented size="small" value={regrouper ? 'grouper' : 'separer'}
              onChange={(v) => setRegrouper(v === 'grouper')}
              options={[
                { value: 'separer', label: 'Une série par article' },
                { value: 'grouper', label: 'Tout regrouper' },
              ]} />
          )}
          <Button onClick={onClose}>Annuler</Button>
          <Button type="primary" disabled={!choisis.length || troppleines} onClick={valider}>
            Ajouter {nbSeries > 0 ? `(${nbSeries} série${nbSeries > 1 ? 's' : ''})` : ''}
          </Button>
        </div>
      </div>
      {troppleines && (
        <p className="cs-alerte">
          {nbSeries} série(s) pour {placesLibres} place(s) libre(s) :
          {placesLibres === 0
            ? " retirez d'abord une série du rapport."
            : ' retirez des articles, ou choisissez « Tout regrouper » pour n’en faire qu’une.'}
        </p>
      )}
    </Modal>
  );
}
